import { useCallback, useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { useAuth } from '@/hooks/useAuth'
import { useAdvertisementMediasByAdId } from '@/hooks/useAdvertisementMediasByAdId'
import { advertisementService } from '@/services/advertisement_service'
import { Uploader } from '@/components/Uploader'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { X, Video } from 'lucide-react'
import { Form } from '@/components/ui/form'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { logger } from '@magnetar/utils'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { apiClient } from '@/libs/api_client'
import { ApiResponse } from '@magnetar/admin-api-interfaces'
import { useViewMode } from '@/stores/viewMode'

interface AdvertisementSettingsProps {
  advertisementId?: number
  onOpenChange: (open: boolean) => void
  onRefresh?: () => void
}

interface FileInfo {
  file_id: number
  uuid: string
  mime_type: string
  original_name: string
  storage_path: string
  size: number
  status: string
  storage_type: string
  created_at: string
}

const isVideoMimeType = (mimeType: string) => {
  return mimeType.startsWith('video/')
}

// Fallback method using file extension if mime_type is not available
const isVideoByExtension = (url: string) => {
  return url.match(/\.(mp4|webm|ogg)$/i) !== null
}

const getMediaUrl = (fileUuid: string) => {
  return `${import.meta.env.VITE_API_URL}/api/files/download/${fileUuid}`
}

// Function to get file information by UUID
const getFileByUuid = async (uuid: string, token: string): Promise<FileInfo | null> => {
  try {
    const response = await apiClient.get<ApiResponse<FileInfo>>(`/api/files/uuid/${uuid}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })

    if (response.data.success) {
      return response.data.data
    }
    return null
  } catch (error) {
    logger.error('Error fetching file info:', error)
    return null
  }
}

export function AdvertisementSettings({ advertisementId, onRefresh }: AdvertisementSettingsProps) {
  const { token } = useAuth()
  const { id: supplierId } = useViewMode()
  const { medias, isLoading, mutate } = useAdvertisementMediasByAdId(supplierId, advertisementId)
  const [uploaderKey, setUploaderKey] = useState(0)
  const [selectedMediaId, setSelectedMediaId] = useState<number | null>(null)
  const [durationDialogOpen, setDurationDialogOpen] = useState(false)
  const [duration, setDuration] = useState('')
  const [fileTypes, setFileTypes] = useState<Record<string, boolean>>({}) // uuid -> isVideo

  const form = useForm()

  // Fetch file information for all media items
  useEffect(() => {
    const fetchFileTypes = async () => {
      if (!token || !medias.length) return

      const newFileTypes: Record<string, boolean> = {}

      for (const media of medias) {
        const fileInfo = await getFileByUuid(media.file_uuid, token)
        if (fileInfo) {
          newFileTypes[media.file_uuid] = isVideoMimeType(fileInfo.mime_type)
        } else {
          // Fallback to extension-based detection if API call fails
          const mediaUrl = getMediaUrl(media.file_uuid)
          newFileTypes[media.file_uuid] = isVideoByExtension(mediaUrl)
        }
      }

      setFileTypes(newFileTypes)
    }

    fetchFileTypes()
  }, [medias, token])

  const handleUploadSuccess = useCallback(
    async (result: { uuid: string; meta?: { videoLength?: number } }) => {
      if (!advertisementId || !token) return

      try {
        const { uuid, meta } = result

        // Get file information to determine type
        const fileInfo = await getFileByUuid(uuid, token)
        let playDurationMs = 0

        if (fileInfo) {
          if (isVideoMimeType(fileInfo.mime_type)) {
            // For videos, use the actual video length
            playDurationMs = meta?.videoLength || 0
          } else {
            // For images, default to 10 seconds
            playDurationMs = 10000 // 10 seconds in milliseconds
          }

          // Update file types state
          setFileTypes((prev) => ({
            ...prev,
            [uuid]: isVideoMimeType(fileInfo.mime_type),
          }))
        } else {
          // Fallback: if we can't get file info, assume it's a video and use meta
          playDurationMs = meta?.videoLength || 0
        }

        const response = await advertisementService.createAdvertisementMedia(
          supplierId,
          advertisementId,
          {
            advertisement_id: advertisementId,
            file_uuid: uuid,
            media_order: medias.length + 1,
            is_active: true,
            play_duration_ms: playDurationMs,
          },
          token,
        )

        if (response.success) {
          toast.success('廣告媒體已新增')
          mutate()
          onRefresh?.()
          setUploaderKey((prev) => prev + 1)
        } else {
          toast.error(response.error?.message || '新增廣告媒體失敗')
        }
      } catch {
        toast.error('新增廣告媒體失敗')
      }
    },
    [advertisementId, token, mutate, medias, onRefresh, supplierId],
  )

  const handleDurationClick = useCallback((mediaId: number, currentDuration?: number) => {
    setSelectedMediaId(mediaId)
    setDuration(currentDuration ? String(Math.round(currentDuration / 1000)) : '10')
    setDurationDialogOpen(true)
  }, [])

  const handleDurationSubmit = useCallback(async () => {
    if (!selectedMediaId || !token || !supplierId) return

    try {
      const durationMs = parseInt(duration) * 1000
      const response = await advertisementService.updateAdvertisementMediaDuration(
        supplierId,
        selectedMediaId,
        durationMs,
        token,
      )

      if (response.success) {
        toast.success('已成功更新播放時長')
        mutate()
        onRefresh?.()
        // Close dialog on success
        setDurationDialogOpen(false)
        setSelectedMediaId(null)
        setDuration('')
      } else {
        toast.error(response.error?.message || '更新播放時長失敗')
      }
    } catch {
      toast.error('更新播放時長失敗')
    }
  }, [selectedMediaId, token, duration, mutate, supplierId, onRefresh])

  const handleDelete = useCallback(
    async (adMediaId: number) => {
      if (!advertisementId || !token) return

      try {
        const response = await advertisementService.deleteAdvertisementMedia(
          supplierId,
          adMediaId,
          token,
        )

        if (response.success) {
          toast.success('廣告媒體已刪除')
          mutate()
          onRefresh?.()
        } else {
          toast.error(response.error?.message || '刪除廣告媒體失敗')
        }
      } catch {
        alert('error')
        toast.error('刪除廣告媒體失敗')
      }
    },
    [advertisementId, token, mutate, onRefresh, supplierId],
  )

  if (isLoading) {
    return <div className="flex items-center justify-center p-6">載入中...</div>
  }

  return (
    <div className="space-y-6 p-6">
      <div>
        <h3 className="text-lg font-medium">廣告媒體</h3>
        <p className="text-sm text-muted-foreground">管理廣告的媒體內容。</p>
      </div>

      <Form {...form}>
        <div className="space-y-4">
          <Card className="p-4">
            {medias.length === 0 ? (
              <Uploader
                key={uploaderKey}
                folder="advertisements"
                onUploadSuccess={handleUploadSuccess}
                allowedFileTypes={['.jpg', '.jpeg', '.png', '.gif', '.webp', '.mp4']}
                maxFileSize={100 * 1024 * 1024}
              />
            ) : (
              <div className="text-center p-4 text-muted-foreground">
                已有廣告媒體，一次只能有一個媒體。若要更換，請先刪除現有媒體。
              </div>
            )}
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {medias.map((media) => {
              const mediaUrl = getMediaUrl(media.file_uuid)
              const isVideo = fileTypes[media.file_uuid]

              return (
                <Card key={media.ad_media_id} className="relative group overflow-hidden">
                  {!isVideo ? (
                    <img
                      src={mediaUrl}
                      alt={`Media ${media.ad_media_id}`}
                      className="w-full h-48 object-cover"
                    />
                  ) : (
                    <div className="relative w-full h-48">
                      <Video className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 h-12 w-12 text-white opacity-80" />
                      <video src={mediaUrl} className="w-full h-48 object-cover" controls />
                    </div>
                  )}
                  <Button
                    variant="destructive"
                    size="icon"
                    className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={() => handleDelete(media.ad_media_id)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                  {!isVideo && (
                    <Badge
                      variant="secondary"
                      className="absolute bottom-2 left-2 cursor-pointer hover:bg-secondary/80"
                      onClick={() => handleDurationClick(media.ad_media_id, media.play_duration_ms)}
                    >
                      {media.play_duration_ms
                        ? `${Math.round(media.play_duration_ms / 1000)}秒`
                        : '無設定秒數'}
                    </Badge>
                  )}
                </Card>
              )
            })}
          </div>
        </div>
      </Form>

      <Dialog open={durationDialogOpen} onOpenChange={setDurationDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>設定播放時長</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="duration" className="text-right">
                播放時長
              </Label>
              <Input
                id="duration"
                type="number"
                min="1"
                className="col-span-3"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                placeholder="請輸入秒數"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDurationDialogOpen(false)}>
              取消
            </Button>
            <Button onClick={handleDurationSubmit}>確認</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
